/**
 * StorePatch.ts — Injects "Add to Hollow" button into Steam Store webview.
 * Click detection: injected script calls console.log, plugin reads it via CDP WebSocket.
 * No HTTP requests needed — bypasses mixed content restrictions.
 */

// ── Config ───────────────────────────────────────────────────────────────────
const POLL_MS = 2000;
const BUTTON_COLOR_BG = "rgba(76, 175, 80, 0.92)";
const BUTTON_COLOR_TEXT = "#fff";
const HOLLOW_TAG = "HOLLOW:";
const RESTART_TAG = "RESTART:";

// ── State ────────────────────────────────────────────────────────────────────
let storeSocket: WebSocket | null = null;
let messageId = 1;
let wsReady = false;
let pollTimer: any = null;
let lastInjectedAppId = "";
let _serverAPI: any = null;
const COLLECTION_TAG = "Added by Hollow";
const addedAppIds = new Set<string>();

// ── Collection helpers ────────────────────────────────────────────────────────
let _hollowCollectionId: string | null = null;

/** Tag an app in the Hollow collection with retry. */
async function tagInHollowCollection(appId: string, retries = 3): Promise<void> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const cs = (window as any).collectionStore || (window as any).g_CollectionStore;
      if (!cs) {
        console.log(`[Hollow] tagInHollowCollection attempt ${attempt + 1}/${retries}: no collectionStore`);
        if (attempt < retries - 1) { await new Promise(r => setTimeout(r, 1000)); continue; }
        notify("Hollow-decky", `Collection store unavailable — key added but not tagged`);
        return;
      }

      let collectionId = _hollowCollectionId || cs.GetCollectionIDByUserTag?.(COLLECTION_TAG);
      let collection = collectionId ? cs.GetCollection?.(collectionId) : null;

      // Create collection if it doesn't exist
      if (!collection) {
        _hollowCollectionId = null;
        console.log("[Hollow] creating collection:", COLLECTION_TAG);
        const coll = cs.NewUnsavedCollection?.(COLLECTION_TAG, undefined, []);
        if (!coll) {
          console.log(`[Hollow] NewUnsavedCollection returned null, attempt ${attempt + 1}`);
          if (attempt < retries - 1) { await new Promise(r => setTimeout(r, 1000)); continue; }
          notify("Hollow-decky", `Could not create collection — key added but not tagged`);
          return;
        }
        await coll.Save?.();
        collectionId = coll.id ?? cs.GetCollectionIDByUserTag?.(COLLECTION_TAG);
        collection = collectionId ? cs.GetCollection?.(collectionId) : null;
        if (!collection) {
          if (attempt < retries - 1) { await new Promise(r => setTimeout(r, 1000)); continue; }
          notify("Hollow-decky", `Collection creation failed — key added but not tagged`);
          return;
        }
      }

      _hollowCollectionId = collectionId as string;

      // Get app overview and add to collection
      const appStore = (window as any).appStore;
      const overview = appStore?.GetAppOverviewByAppID?.(parseInt(appId, 10));

      if (overview) {
        // Preferred method: AddApps via DragDropCollection
        const ddCollection = collection.AsDragDropCollection?.();
        if (ddCollection?.AddApps) {
          ddCollection.AddApps([overview]);
          await collection.Save?.();
          console.log("[Hollow] tagged app", appId, "via AddApps (attempt", attempt + 1, ")");
          return;
        }
      }

      // Fallback: direct set manipulation
      if (collection.m_setApps && collection.m_setAddedManually) {
        const appIdNum = parseInt(appId, 10);
        if (!collection.m_setApps.has(appIdNum)) {
          collection.m_setApps.add(appIdNum);
          collection.m_setAddedManually.add(appIdNum);
          await collection.Save?.();
          console.log("[Hollow] tagged app", appId, "via set manipulation (attempt", attempt + 1, ")");
          return;
        }
        console.log("[Hollow] app", appId, "already in collection");
        return;
      }

      console.log(`[Hollow] tagInHollowCollection: no method available, attempt ${attempt + 1}`);
      if (attempt < retries - 1) { await new Promise(r => setTimeout(r, 1000)); }
    } catch (e) {
      console.log(`[Hollow] tagInHollowCollection attempt ${attempt + 1}/${retries} error:`, e);
      _hollowCollectionId = null;
      if (attempt < retries - 1) { await new Promise(r => setTimeout(r, 1000)); }
    }
  }
  notify("Hollow-decky", `Failed to add game to collection after ${retries} tries`);
}

function notify(title: string, body: string): void {
  _serverAPI?.toaster?.toast?.({ title, body, duration: 5000 });
}

interface StoreTab {
  webSocketDebuggerUrl: string;
  url: string;
}

function extractAppId(url: string): string {
  const m = url?.match(/\/app\/(\d+)/);
  return m?.[1] ?? "";
}

// ── CDP WebSocket ────────────────────────────────────────────────────────────
function wsSend(obj: Record<string, unknown>): void {
  if (!storeSocket || storeSocket.readyState !== WebSocket.OPEN) return;
  storeSocket.send(JSON.stringify(obj));
}

async function inject(appId: string, force = false): Promise<void> {
  if (appId === lastInjectedAppId && !force) return;

  let showRestart = true;
  let buttonLayout = "inline";
  try {
    const s = await _serverAPI?.callPluginMethod("get_setting", { key: "show_restart_button", default: true });
    showRestart = !(s?.success && s.result === false);
  } catch {}
  try {
    const l = await _serverAPI?.callPluginMethod("get_setting", { key: "button_layout", default: "inline" });
    if (l?.success) buttonLayout = l.result || "inline";
  } catch {}

  lastInjectedAppId = appId;
  const added = addedAppIds.has(appId);

  // Inline layout: buttons below tags, gamepad-focusable
  const inlineScript = `(function(){
var old=document.getElementById('hollow-decky-btns');
if(old)old.remove();
function m(t,tag,busy,disabled){var b=document.createElement('button');b.textContent=t;b.type='button';b.style.cssText='background:${BUTTON_COLOR_BG};color:${BUTTON_COLOR_TEXT};padding:8px 16px;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,0.4);text-align:center;min-width:200px;border:2px solid rgba(255,255,255,0.3);outline:none;display:block;width:100%;transition:background 0.15s,border-color 0.15s';if(disabled){b.style.opacity='0.6';b.style.pointerEvents='none';}else{b.onfocus=function(){b.style.borderColor='#fff';b.style.background='rgba(76,175,80,1)';};b.onblur=function(){b.style.borderColor='rgba(255,255,255,0.3)';b.style.background='${BUTTON_COLOR_BG}';};b.onclick=function(){b.textContent=busy;b.style.opacity='0.6';b.style.pointerEvents='none';console.log(tag+${appId});};}return b;}
var tags=document.querySelector('.glance_tags_ctn')||document.querySelector('.popular_tags_ctn')||document.querySelector('.glance_tags');
if(!tags){setTimeout(function(){var old2=document.getElementById('hollow-decky-btns');if(old2)return;var t2=document.querySelector('.glance_tags_ctn')||document.querySelector('.popular_tags_ctn')||document.querySelector('.glance_tags');if(!t2){document.body.appendChild(w);return;}t2.parentNode.insertBefore(w,t2.nextSibling);},2000);return;}
var w=document.createElement('div');w.id='hollow-decky-btns';w.style.cssText='display:flex;flex-direction:column;gap:6px;margin:12px 0;padding:10px;background:rgba(0,0,0,0.5);border-radius:8px;font-family:Arial,sans-serif;user-select:none;-webkit-user-select:none';
w.appendChild(m('${added ? "ADDED" : "ADD TO HOLLOW"}','${HOLLOW_TAG}','ADDING...',${added ? "true" : "false"}));
${showRestart ? `w.appendChild(m('RESTART STEAM','${RESTART_TAG}','RESTARTING...',false));` : ""}
tags.parentNode.insertBefore(w,tags.nextSibling);
})();`;

  // Original layout: fixed bottom-left corner
  const originalScript = `(function(){
var s=document.getElementById('hollow-decky-btns');
if(s)s.remove();
s=document.createElement('div');s.id='hollow-decky-btns';
s.style.cssText='position:fixed;bottom:24px;left:16px;z-index:999999;display:flex;flex-direction:column;gap:8px;font-family:Arial,sans-serif;user-select:none;-webkit-user-select:none';
function m(t,tag,busy,disabled){var b=document.createElement('div');b.textContent=t;b.style.cssText='background:${BUTTON_COLOR_BG};color:${BUTTON_COLOR_TEXT};padding:8px 16px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,0.4);text-align:center;min-width:150px';if(disabled){b.style.opacity='0.6';b.style.pointerEvents='none';}else{b.onclick=function(){b.textContent=busy;b.style.opacity='0.6';b.style.pointerEvents='none';console.log(tag+${appId});};}return b;}
s.appendChild(m('${added ? "ADDED" : "ADD TO HOLLOW"}','${HOLLOW_TAG}','ADDING...',${added ? "true" : "false"}));
${showRestart ? `s.appendChild(m('RESTART STEAM','${RESTART_TAG}','RESTARTING...',false));` : ""}
document.body.appendChild(s);
})();`;

  const script = buttonLayout === "original" ? originalScript : inlineScript;
  wsSend({ id: messageId++, method: "Runtime.evaluate", params: { expression: script } });
}

function remove(): void {
  lastInjectedAppId = "";
  wsSend({ id: messageId++, method: "Runtime.evaluate", params: { expression: "(function(){var s=document.getElementById('hollow-decky-btns');if(s)s.remove();})();" } });
}

// ── Connect ──────────────────────────────────────────────────────────────────
async function connect(tab: StoreTab): Promise<void> {
  if (!tab.webSocketDebuggerUrl) return;
  if (storeSocket) {
    try { storeSocket.close(); } catch {}
    storeSocket = null;
    wsReady = false;
  }
  storeSocket = new WebSocket(tab.webSocketDebuggerUrl);
  storeSocket.onopen = () => {
    wsSend({ id: messageId++, method: "Page.enable" });
    wsSend({ id: messageId++, method: "Runtime.enable" });
    setTimeout(() => {
      wsReady = true;
      const appId = extractAppId(tab.url);
      if (appId) inject(appId);
    }, 1500);
  };
  storeSocket.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data);
      // Handle navigation
      if (data.method === "Page.frameNavigated" && data.params?.frame) {
        const frame = data.params.frame;
        if (!frame.parentId) {
          setTimeout(() => {
            const appId = extractAppId(frame.url || "");
            if (appId) inject(appId); else remove();
          }, 2000);
        }
      }
      // Handle console.log from injected buttons.
      if (data.method === "Runtime.consoleAPICalled" && data.params?.args) {
        for (const arg of data.params.args) {
          if (arg.type === "string" && arg.value) {
            if (arg.value.startsWith(HOLLOW_TAG)) {
              const appId = arg.value.slice(HOLLOW_TAG.length);
              _serverAPI?.callPluginMethod("inject_game", { app_id: appId }).then((r: any) => {
                if (r?.success && !r.result?.error) {
                  addedAppIds.add(appId);
                  tagInHollowCollection(appId);
                  const name = r.result?.name || appId;
                  const extra = r.result?.delisted ? " (delisted)" : "";
                  const depotInfo = r.result?.depots ? ` — ${r.result.depots.length} depot(s)` : "";
                  notify("Hollow-decky", `Added ${name}${extra}${depotInfo}`);
                } else {
                  notify("Hollow-decky", r?.result?.error || `Failed to add game ${appId}`);
                }
                if (wsReady && storeSocket?.readyState === WebSocket.OPEN) {
                  inject(appId, true);
                }
              });
            } else if (arg.value.startsWith(RESTART_TAG)) {
              _serverAPI?.callPluginMethod("restart_steam", {});
            }
          }
        }
      }
    } catch {}
  };
  storeSocket.onerror = () => { wsReady = false; };
  storeSocket.onclose = () => { storeSocket = null; wsReady = false; };
}

// ── Poll ─────────────────────────────────────────────────────────────────────
async function pollCdp(): Promise<void> {
  if (!_serverAPI) return;
  try {
    const res = await _serverAPI.callPluginMethod("discover_store_tab", {});
    if (res?.success && res.result) {
      if (!storeSocket || storeSocket.readyState !== WebSocket.OPEN) {
        await connect(res.result);
      }
    } else {
      if (storeSocket) {
        remove();
        try { storeSocket.close(); } catch {}
        storeSocket = null;
        wsReady = false;
        lastInjectedAppId = "";
      }
    }
  } catch {}
}

function startPoll(): void {
  stopPoll();
  pollCdp();
  pollTimer = setInterval(pollCdp, POLL_MS);
}

function stopPoll(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (storeSocket) { remove(); try { storeSocket.close(); } catch {} storeSocket = null; wsReady = false; }
  lastInjectedAppId = "";
}

export function initStorePatch(serverAPI: any): () => void {
  _serverAPI = serverAPI;
  startPoll();
  return () => { stopPoll(); };
}
